import { newElement } from './editing'
import type { LayoutDocument, LayoutElement } from './types'

/**
 * Where a new playlist goes on a hall board: the largest free space, since the
 * middle of every board we ship is the called numbers. A playlist over them is
 * a warning in the header (mediaWarnings), not a refusal.
 */

export interface Rect {
  x: number
  y: number
  w: number
  h: number
}

/** A playlist smaller than this share of the canvas is too small to read from a hall. */
export const MIN_PLAYLIST_SHARE = 0.08
/** Films and most pictures are 16:9; a box of that shape letterboxes least. */
export const PLAYLIST_ASPECT = 16 / 9
export const PLAYLIST_MARGIN = 24
/** Smaller than this a playlist is a postage stamp, and not worth placing. */
const MIN_W = 64
const MIN_H = 36

/** Strict AABB, as covers() in media.ts: boxes that share an edge do not overlap. */
export function overlaps(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h
}

/** Every maximal empty rectangle between two vertical lines of the obstacles' edges. */
export function freeRectangles(canvas: { width: number; height: number }, obstacles: readonly Rect[]): Rect[] {
  const { width, height } = canvas
  const clampX = (v: number) => Math.max(0, Math.min(width, v))
  const clampY = (v: number) => Math.max(0, Math.min(height, v))
  const xs = [...new Set([0, width, ...obstacles.flatMap((o) => [clampX(o.x), clampX(o.x + o.w)])])].sort(
    (a, b) => a - b,
  )
  const out: Rect[] = []
  for (let i = 0; i < xs.length; i++) {
    for (let j = i + 1; j < xs.length; j++) {
      const x1 = xs[i]!
      const x2 = xs[j]!
      // Strict, like overlaps(): an obstacle whose edge is on the line does not block.
      const blockers = obstacles
        .filter((o) => o.x < x2 && x1 < o.x + o.w)
        .map((o) => [clampY(o.y), clampY(o.y + o.h)] as const)
        .sort((a, b) => a[0] - b[0])
      let cursor = 0
      for (const [top, bottom] of blockers) {
        if (top > cursor) out.push({ x: x1, y: cursor, w: x2 - x1, h: top - cursor })
        cursor = Math.max(cursor, bottom)
      }
      if (height > cursor) out.push({ x: x1, y: cursor, w: x2 - x1, h: height - cursor })
    }
  }
  return out
}

/** The largest PLAYLIST_ASPECT box inside r, inset by the margin where r allows it, centred, integer. */
export function fitAspect(r: Rect): Rect {
  // The margin keeps a playlist off its neighbours. On an axis too narrow for it
  // the margin is dropped rather than the box, which would otherwise vanish.
  const mx = r.w > 2 * PLAYLIST_MARGIN ? PLAYLIST_MARGIN : 0
  const my = r.h > 2 * PLAYLIST_MARGIN ? PLAYLIST_MARGIN : 0
  const iw = r.w - 2 * mx
  const ih = r.h - 2 * my
  const w = Math.min(iw, ih * PLAYLIST_ASPECT)
  const h = w / PLAYLIST_ASPECT
  const left = r.x + mx + (iw - w) / 2
  const top = r.y + my + (ih - h) / 2
  // Rounded inwards on every side, so the box never grows past its free rectangle.
  const x = Math.ceil(left)
  const y = Math.ceil(top)
  return { x, y, w: Math.max(0, Math.floor(left + w) - x), h: Math.max(0, Math.floor(top + h) - y) }
}

/** The biggest box the free space among these obstacles holds, or null for none. */
function largestBox(doc: LayoutDocument, obstacles: readonly Rect[]): Rect | null {
  let best: Rect | null = null
  for (const r of freeRectangles(doc.canvas, obstacles)) {
    const box = fitAspect(r)
    if (box.w < MIN_W || box.h < MIN_H) continue
    if (!best || box.w * box.h > best.w * best.h) best = box
  }
  return best
}

export function placePlaylist(doc: LayoutDocument): { element: LayoutElement; cramped: boolean } {
  const base = newElement('media', doc)
  const canvasArea = doc.canvas.width * doc.canvas.height

  // First, room that touches nothing and is big enough to read.
  const clear = largestBox(doc, doc.elements)
  if (clear && clear.w * clear.h >= MIN_PLAYLIST_SHARE * canvasArea) {
    return { element: { ...base, ...clear }, cramped: false }
  }

  // Then room that touches only what may be moved. On Split the recent calls are a
  // calls element too, so they are kept clear as well, and so is a playlist already there.
  const calls = doc.elements.filter((el) => el.kind === 'calls')
  const beside =
    largestBox(doc, doc.elements.filter((el) => el.kind === 'calls' || el.kind === 'media')) ?? largestBox(doc, calls)
  if (beside) return { element: under(doc, { ...base, ...beside }), cramped: true }

  // The calls fill the board. Put it where the palette always did; the editor's
  // problems list then says it covers the calls, which is the truth.
  return { element: base, cramped: true }
}

/**
 * A cramped playlist drawn beneath whatever it overlaps.
 *
 * On Split the space it finds is under the clock and the "Before this" label, and
 * a new element is normally put above everything, which would hide both until the
 * designer moved it. Beneath them, the board still reads while the designer makes
 * room. Depth has no lower bound on either side (render.tsx, layout.Sorted), so
 * one below zero is fine.
 */
function under(doc: LayoutDocument, el: LayoutElement): LayoutElement {
  const covered = doc.elements.filter((other) => overlaps(el, other))
  if (covered.length === 0) return el
  return { ...el, z: Math.min(...covered.map((other) => other.z ?? 0)) - 1 }
}

/** Whether the playlist with this id still wants the hint: it overlaps another element or
 *  is under MIN_PLAYLIST_SHARE of the canvas. False once it has gone. */
export function stillCramped(doc: LayoutDocument, id: string): boolean {
  const el = doc.elements.find((e) => e.id === id)
  if (!el) return false
  if (el.w * el.h < MIN_PLAYLIST_SHARE * doc.canvas.width * doc.canvas.height) return true
  return doc.elements.some((other) => other.id !== id && overlaps(el, other))
}

/** Whether starting from the playlist template would help: only on a board with no playlist yet. */
export function templateHelps(doc: LayoutDocument): boolean {
  return !doc.elements.some((el) => el.kind === 'media')
}
